import moment from 'moment';
import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  memo,
  ReactNode,
  ReactElement,
} from 'react';
import { Upload, Text, Form, Modal, Button, Icon } from 'tea-component';
import useCos from './useCOS';
import { useSelector } from 'umi';
import errorImg from '@/pages/dashboard/assets/images/funnel/step.png';
import { useAigcIntl } from '@/pages/aigc/utils/utils';
import './index.scss';
interface Props {
  type?: string; // 上传的是图片还是文件
  customStyle?: React.CSSProperties;
  isShowImg?: boolean;
  defaultValue?: any;
  getUploadSuccessMessage:
  | ((cloudRes: any, file: any, uploadDate: string, fileSize: string) => void)
  | null;
  getUploadStatus: ((status: string | undefined) => void) | null;
  tips?: string; // 上传提示
  accept?: string | string[]; // 上传文件类型限制
  fileSize?: number; // 上传文件大小限制
  CustomizeElementContent?: ReactNode;
  [keyName: string]: any;
}

const DEFAULT_PROPS: Props = {
  type: 'image',
  customStyle: {},
  isShowImg: true,
  defaultValue: null,
  getUploadSuccessMessage: null,
  tips: 'Please upload PNG image, the size is less than 1 MB',
  accept: 'image/png, image/jpeg, image/jpg',
  // accept: 'image/png',
  fileSize: 1024 * 1024 * 1,
};

/**
 * 计算文件大小
 * @param v 文件大小数字
 * @returns 转换单位后的结果
 */
export const getFileSize = (v: number) => {
  if (!v) return '';
  const g = Math.round(v / (1024 * 1024 * 1024));
  const m = Math.round(v / (1024 * 1024));
  const k = Math.round(v / 1024);
  if (g > 1) return `${g  }G`;
  if (m > 1) return `${m  }MB`;
  return `${k  }K`;
};

function FormUpload(props: Props) {
  const {
    type,
    customStyle,
    isShowImg,
    defaultValue,
    getUploadSuccessMessage,
    tips,
    accept,
    fileSize,
    defaultInfo,
    getUploadStatus,
    action,
    CustomizeElementContent,
    handleExternalonStart,
    handleExternalonCancel,
    ...restProps
  } = props;
  const { putObject, getObjectUrl } = useCos({});
  const { userInfo } = useSelector((state: any) => {
    const { userInfo } = state.home;
    return {
      userInfo,
    };
  });
  const { formatMessage, isEn } = useAigcIntl('upload');
  // 控制显示默认数据卡片
  const [isShowDefaultCard, setIsShowDefaultCard] = useState<boolean>(false);
  // 上传文件信息
  const [file, setFile] = useState<any | null>(null);
  // 上传时本地图片
  const [image, setImage] = useState(undefined);
  // 上传之后得到的腾讯云返回信息
  const [uploadResMessage, setUploadResMessage] = useState<any>(null);
  // 上传时的时间
  const [uploadDate, setUploadDate] = useState<string>('');
  // 上传状态
  const [status, setStatus] = useState<string | undefined>(undefined);
  // 控制上传区域上传失败样式，false表示不允许上传
  const isAccepted = useRef<boolean>(true);
  // 上传进度
  const [percents, setPercents] = useState<number>(0);
  // 用户是否在上传过程中点击取消上传
  const isClickCancelUpload = useRef<boolean>(false);
  useEffect(() => {
    if (defaultInfo?.imgUrl) {
      const fileName = defaultInfo?.imgUrl?.match(/\/([^/]+)$/)?.[1];
      setFile(
        new File([], fileName, {
          lastModified: new Date(defaultInfo?.createTime).getTime(),
        }),
      );
      setImage(defaultInfo?.imgUrl);
      setStatus('success');
      setPercents(100);
    }
  }, [defaultInfo?.imgUrl]);

  // console.log("** file", file, image, status, percents)

  // onStart开始上传
  const handleStart = useCallback(
    (file: File, { xhr }: { xhr: XMLHttpRequest; }) => {
      // console.log(file, xhr);
      setFile(file);
      getBase64(file);
      handleExternalonStart?.();
    },
    [],
  );
  // onSuccess上传成功
  // const handleSuccess = useCallback((result: object | string) => {
  // }, []);

  // onError上传失败
  // const handleError = useCallback((error) => {
  // }, []);

  // beforeUpload上传前操作
  const beforeUpload = useCallback(
    async (file: File, fileList: File[], isAccepteds: boolean) => {
      // 当图片卡片在显示时，隐藏
      isShowDefaultCard && setIsShowDefaultCard(false);

      const isError = fileIsError(file);
      isAccepted.current = !isError;
      // 文件不符合要求
      if (isError) return false;

      // 允许上传时，把相关的状态恢复成原始状态
      isClickCancelUpload.current = false;
      setUploadResMessage(null);
      return true;
    },
    [isShowDefaultCard],
  );

  // 点击Cancel Upload或Delete取消上传
  const handleAbort = useCallback(() => {
    setFile(null);
    setUploadResMessage(null);
    setStatus(undefined);
    setPercents(0);
    isAccepted.current = true;
    isClickCancelUpload.current = true;
    getUploadSuccessMessage?.(null, null, '', '');
    handleExternalonCancel?.();
  }, []);

  // 上传进度回调
  const onProgress = useCallback(({ percent }: { percent: number; }) => {
    // console.log("percent", percent, status);
    setPercents(Math.round(percent * 100));
    if (percent < 1 && status !== 'validating') {
      setStatus('validating');
      getUploadStatus?.('validating');
    }
  }, []);

  // 上传的文件转base64格式
  const getBase64 = useCallback(
    async (file: File) => {
      if (!isAccepted.current) return;
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result as string);
      };
      reader.readAsDataURL(file);
      const time = new Date().getTime();
      // 上传图片
      await putObject({
        // path: `${type}/${file?.name}`,
        path: `/aitool/tool/${userInfo?.userid}/${`${time  }_${  file?.name}`}`,
        file,
        onProgress,
      });
      // 用户在上传中点击取消上传
      if (isClickCancelUpload.current) {
        isClickCancelUpload.current = false;
        setStatus(undefined);
        getUploadStatus?.(undefined);
        return;
      }
      // 拿到上传图片的链接
      // const res: any = await getObjectUrl({ path: `/aitool/tool/${userInfo?.userid}/${time}/${file?.name}` });
      // console.log('finishUploadImage', res);
      setUploadResMessage(
        `/aitool/tool/${userInfo?.userid}/${`${time  }_${  file?.name}`}`,
      );
      setStatus('success');
      getUploadStatus?.('success');
    },
    [status, userInfo],
  );

  // 上传文件是否不符合要求
  const fileIsError = useCallback((file: any, type: any = undefined) => {
    if (file) {
      // 匹配文件后缀名
      const fileSuffix = file?.name?.slice(file?.name?.lastIndexOf('.'));
      // console.log(file, fileSize, accept, fileSuffix);
      if (
        file?.size > (fileSize as number) ||
        !file?.type ||
        (accept?.indexOf(file?.type) === -1 &&
          accept?.indexOf(fileSuffix) === -1)
      ) {
        setStatus('error');
        setUploadResMessage(null);
        getUploadStatus?.('error');
        return true;
      }
      return false;
    }
    return false;
  }, []);

  // 图片上传完成改变相应的值
  useEffect(() => {
    if (!uploadResMessage) return;
    const uploadDate = moment().format('YYYY-MM-DD HH:mm:ss');
    setUploadDate(uploadDate);
    const fileSize = getFileSize(file?.size);
    getUploadSuccessMessage?.(uploadResMessage, file, uploadDate, fileSize);
  }, [uploadResMessage]);
  const BottomDefauletView = ({
    open,
  }: {
    open: (...args: any[]) => void;
  }): ReactElement => {
    if (CustomizeElementContent) {
      return <div onClick={open}>{CustomizeElementContent}</div>;
    }
    return (
      <div style={isEn ? { width: '220px' } : {}}>
        <a onClick={open}>{formatMessage('click')}</a>
        <Text theme="weak">/{formatMessage('drag')}</Text>
      </div>
    );
  };
  return (
    <Form.Control
      status={status}
      message={formatMessage('drag_tips')}
      className="upload-container"
    >
      <Upload
        action={action}
        // action="https://run.mocky.io/v3/68ed7204-0487-4135-857d-0e4366b2cfad"
        accept={accept}
        maxSize={1024 * 1024}
        onStart={handleStart}
        beforeUpload={beforeUpload}
        // onProgress={handleProgress}
        // onSuccess={handleSuccess}
        // onError={handleError}
        {...restProps}
      >
        {({ open, isDragging }) => (
          <Upload.Dragger
            className={`${
              status === 'success' ? '' : 'tea-form-upload-drag-error'
            }`}
            filename={
              file && (
                <>
                  <div className="icon-defaulet">
                    {file && file.name}&nbsp;&nbsp;
                    {status !== 'validating' && (
                      <Icon type={status === 'success' ? 'success' : 'error'} />
                    )}
                  </div>
                </>
              )
            }
            image={isShowImg ? '' : status === 'success' ? image : errorImg}
            percent={percents}
            description={
              file && (
                <>
                  <p>
                    {formatMessage('file_size')}：{Math.floor(file.size / 1024)}
                    K
                  </p>
                  <p>
                    {formatMessage('file_day')}：
                    {moment(file.lastModifiedDate).format(
                      'YYYY-MM-DD HH:mm:ss',
                    )}
                  </p>
                </>
              )
            }
            button={
              status === 'validating' ? (
                <Button type="link" onClick={handleAbort}>
                  {formatMessage('file_cancel')}
                </Button>
              ) : (
                <>
                  <Button type="link" onClick={open}>
                    {formatMessage('file_reupload')}
                  </Button>
                  <Button
                    type="link"
                    style={{ marginLeft: 8 }}
                    onClick={handleAbort}
                  >
                    {formatMessage('file_delete')}
                  </Button>
                </>
              )
            }
          >
            {isDragging ? '释放鼠标' : <BottomDefauletView {...{ open }} />}
          </Upload.Dragger>
        )}
      </Upload>
    </Form.Control>
  );
}

FormUpload.defaultProps = DEFAULT_PROPS;

export default memo(FormUpload);
